import React from "react"
import Button from "@love-irish/core-button"
import Tooltip from "design_system/core/components/tooltip"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@love-irish/ui-components/dropdown-menu"
import { IconDots } from "@tabler/icons-react"
import type { Icon } from "@tabler/icons-react"

// =============================================================================
// Slots — markers that render nothing; the container reads their props and
// places them. Same compound pattern as @love-irish/resource-contribution-prompt's
// ContributionSection (.Data / .Form / ...).
// =============================================================================

export interface ResourceMenuItemProps {
  /** Toolbar icon (omit when using `children`). */
  icon?: Icon
  /** Icon shown when `active` is true, e.g. a filled star. Falls back to `icon`. */
  activeIcon?: Icon
  /** Toggled/active state (e.g. saved) — drives the active icon + styling. */
  active?: boolean
  /**
   * Active-state color treatment. `"warning"` (default) is the amber save/star tint; `"brand"` paints the
   * icon with the love.irish "using this" gradient (matches the library sidebar's Using-this card).
   */
  activeColor?: "warning" | "brand"
  /** Accessible label + tooltip text. */
  label: string
  /** Optional subtext under the label in the tooltip (the "why" of the action). Doesn't affect aria-label. */
  description?: React.ReactNode
  onClick?: () => void
  href?: string
  disabled?: boolean
  /** Custom content instead of the default icon button (e.g. a connected dropdown). */
  children?: React.ReactNode
}

export interface ResourceMenuMenuItemProps {
  icon?: Icon
  label: string
  onClick?: () => void
  href?: string
  /** Open the href in a new tab. */
  newTab?: boolean
  /** Treat the href as a download (e.g. an export). */
  download?: boolean
  /** Destructive (red) styling, e.g. Report. */
  destructive?: boolean
}

const Item = (_props: ResourceMenuItemProps) => null
const MenuItem = (_props: ResourceMenuMenuItemProps) => null

// Active-icon color treatment. Base transition-colors gives the inactive→active tint a smooth fade;
// the "brand" variant matches the library sidebar's Using-this gradient card. Motion is opt-in via
// `motion-safe:` and the app's global prefers-reduced-motion reset (application.css) already tames it.
const ACTIVE_ICON_COLOR: Record<
  NonNullable<ResourceMenuItemProps["activeColor"]>,
  string
> = {
  warning: "text-warning-500",
  brand: "text-earth-500",
}

const iconClassName = (
  active?: boolean,
  activeColor: NonNullable<ResourceMenuItemProps["activeColor"]> = "warning"
) =>
  [
    "inline-block transition-colors duration-200",
    active ? ACTIVE_ICON_COLOR[activeColor] : "",
    // Subtle pop on activation; skipped for users who prefer reduced motion.
    active ? "motion-safe:animate-in motion-safe:zoom-in-75 motion-safe:duration-200" : "",
  ]
    .filter(Boolean)
    .join(" ")

const collectSlots = (children: React.ReactNode, type: React.ElementType) => {
  const out: React.ReactElement<any>[] = []
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child) && child.type === type) out.push(child)
  })
  return out
}

// =============================================================================
// Container
// =============================================================================

export interface ResourceMenuProps {
  /** `.Item` (toolbar buttons) and `.MenuItem` (overflow menu) slots. */
  children?: React.ReactNode
  className?: string
}

/**
 * A resource's action menu: primary actions as icon buttons in a row, plus an overflow
 * "…" menu for secondary actions. Declarative and reusable via slots, so any resource
 * surface composes the actions it needs and wires their state.
 *
 * ```tsx
 * <ResourceMenu>
 *   <ResourceMenu.Item icon={IconStar} activeIcon={IconStarFilled} active={saved}
 *     label={saved ? "Saved" : "Save"} onClick={toggleSave} />
 *   <ResourceMenu.Item label="Add to collection"><AddToCollection itemId={id} /></ResourceMenu.Item>
 *   <ResourceMenu.Item icon={IconShare} label="Share" onClick={share} />
 *   <ResourceMenu.MenuItem icon={IconLink} label="Copy link" onClick={copyLink} />
 *   <ResourceMenu.MenuItem icon={IconFlag} label="Report" onClick={report} destructive />
 * </ResourceMenu>
 * ```
 */
export const ResourceMenu = ({ children, className }: ResourceMenuProps) => {
  const items = collectSlots(children, Item)
  const menuItems = collectSlots(children, MenuItem)

  return (
    <div className={`flex items-center gap-1 ${className ?? ""}`}>
      {items.map((item, i) => {
        const p = item.props as ResourceMenuItemProps

        // Custom content (e.g. a connected dropdown) brings its own trigger/tooltip.
        if (p.children) {
          return <React.Fragment key={i}>{p.children}</React.Fragment>
        }

        const Ico = p.active && p.activeIcon ? p.activeIcon : p.icon
        const tooltipTitle: React.ReactNode = p.description ? (
          <span className="block">
            <span className="block">{p.label}</span>
            <span className="block text-xs opacity-80">{p.description}</span>
          </span>
        ) : (
          p.label
        )
        return (
          <Tooltip key={i} title={tooltipTitle}>
            <Button
              size="sm"
              variant="ghost"
              color="secondary"
              aria-label={p.label}
              aria-pressed={p.active}
              disabled={p.disabled}
              onClick={p.onClick}
              href={p.href}
            >
              {Ico ? (
                <Ico
                  size={16}
                  className={iconClassName(p.active, p.activeColor)}
                />
              ) : (
                p.label
              )}
            </Button>
          </Tooltip>
        )
      })}

      {menuItems.length > 0 && (
        <DropdownMenu>
          <Tooltip title="More actions">
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                color="secondary"
                aria-label="More actions"
              >
                <IconDots size={16} className="inline-block" />
              </Button>
            </DropdownMenuTrigger>
          </Tooltip>
          <DropdownMenuContent align="end">
            {menuItems.map((mi, i) => {
              const p = mi.props as ResourceMenuMenuItemProps
              const MIco = p.icon
              const inner = (
                <>
                  {MIco && <MIco size={16} className="mr-2 inline-block" />}
                  {p.label}
                </>
              )
              return (
                <DropdownMenuItem
                  key={i}
                  onClick={p.onClick}
                  asChild={!!p.href}
                  className={p.destructive ? "text-red-600" : undefined}
                >
                  {p.href ? (
                    <a
                      href={p.href}
                      download={p.download || undefined}
                      target={p.newTab ? "_blank" : undefined}
                      rel={p.newTab ? "noopener noreferrer" : undefined}
                    >
                      {inner}
                    </a>
                  ) : (
                    inner
                  )}
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}

ResourceMenu.Item = Item
ResourceMenu.MenuItem = MenuItem

export default ResourceMenu

// Higher-level, configured menu of standard actions (star / share / edit / …) built on the slots above.
export {
  ResourceActionMenu,
  type ResourceActionMenuProps,
  type ResourceActionKey,
} from "./action_menu"
